import { connect } from 'react-redux';
import { isEmpty } from 'lodash';
import i18next from 'i18next';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  canCreateAndEditDraftExternalOrder,
  canDeleteDraftExternalOrder,
  canEditToComeAndInPreparationExternalOrder,
  canRevertExternalOrderToDraft,
  canRevertExternalOrderToInPreparation,
} from '@selectors/actions/externalOrderActions';
import { getAuthorizedActions } from '@selectors/featureProps';
import { getClientInfo } from '@selectors/client';
import { loading, loadingSuccess } from '@actions/loading';
import { openGenericModal } from '@actions/modal';
import { showErrorMessage } from '@actions/messageconfirmation';

import { useExternalOrdersContext } from '@context/ExternalOrdersContext';
import { useListRowActions } from '@hooks/useListRowActions';

import { externalOrder as externalOrderService } from '@services/externalOrder';

import { Button, ListView } from '@commons/utils/styledLibraryComponents';
import { EXTERNAL_FEATURE_PATHS } from '@commons/utils/featurePaths';
import { getClientStoreNameTranslation } from '@commons/utils/translations';
import DeepsightFiltersButton from '@commons/DeepsightAnalyticsHeaders/components/DeepsightFiltersButton';
import EmptyState from '@commons/EmptyState';
import GeneralEmptyStateListView from '@commons/GeneralEmptyStateListView';
import NavigationBreadCrumb from '@commons/Breadcrumb/NavigationBreadCrumb';

import StatusTabs from './components/StatusTabs';

import { buildOrdersListReturnState } from './utils/ordersListReturnLocation';
import {
  DUPLICATED_ORDER_STATE_KEY,
  EXTERNAL_ORDER_STATUS,
  EXTERNAL_ORDER_TAB,
  EXTERNAL_ORDERS_PATH,
  FIRST_PAGE,
  getExternalOrderTabs,
} from './constants';
import { exportExternalOrders } from './utils/export';
import { formatExternalOrders } from './utils/formatExternalOrders';
import { getActions, getRowActions } from './utils/getActions';
import {
  getDeleteOrderModal,
  getRevertToDraftModal,
  getRevertToInPreparationModal,
  getRevertToToComeModal,
} from './utils/getExternalOrderModals';
import { getExternalOrderColumns } from './utils/getExternalOrderColumns';
import { getExternalOrderFilterList } from './utils/getExternalOrderFilterList';
import { useExternalClientsOfStores } from './hooks/useExternalClientsOfStores';
import { useExternalOrdersListParams } from './hooks/useExternalOrdersListParams';
import { usePersistedSelectedStores } from './hooks/usePersistedSelectedStores';

import { Content, PageContainer } from './styledComponents';

/**
 * ListView reads its query params from the page instead of filtering client-side. The callback has
 * to keep a stable identity: ListView watches it to reset its own row state, so an inline arrow
 * would drop the current selection on every render.
 */
const enableServerSideQuerying = () => true;

/**
 * Statuses telling the deleted row was already stale: the order is gone or no longer reachable
 * (404), or it left the draft status since the list was loaded (409). Both mean the displayed row
 * does not reflect the server anymore, so the list is resynchronized instead of being left as is.
 */
const STALE_ORDER_ERROR_STATUSES = [404, 409];

/**
 * Whether a condition descriptor still misses an operand. A partially filled advanced filter — an
 * incomplete time for instance — resolves to null, and such a condition is dropped rather than sent.
 *
 * @param {Object} condition - The condition descriptor (e.g. `{ between: [...] }`)
 *
 * @returns {boolean} Whether one of its operands is missing
 */
const hasMissingOperand = (condition) =>
  Object.values(condition).some((operand) =>
    Array.isArray(operand) ? operand.some((value) => value == null) : operand == null,
  );

/**
 * External orders list (/external/orders). Consultation only needs the page to be associated with
 * the user role; the creation and every end-of-row transition are gated by their own right and
 * hidden without it. The tabs are a narrowing of the `status` condition and cumulate with the
 * search and the filters, which the list route resolves server-side.
 *
 * The rows come from POST /v2/b2b/external-orders/paginated (COR-1681), the deletion from
 * DELETE /v2/b2b/external-orders/:id (COR-1684), the status transitions from
 * PATCH /v2/b2b/external-orders/:id/status (COR-1686) and the duplication opens the creation form
 * on the payload of GET /v2/b2b/external-orders/:id/duplicate (COR-1691).
 */
export const ExternalOrders = (props) => {
  const {
    history,
    match: { path },
    stores,
    areStoresLoading,
    storeName,
    channels,
    hasMultipleChannels,
    currency,
    userLanguageCode,
    canCreateDraft,
    canDeleteDraft,
    canRevertToDraft,
    canEditToComeAndInPreparation,
    canRevertToInPreparation,
    openModal,
    displayErrorMessage,
    pageLoading,
    pageLoaded,
  } = props;

  const listViewRef = useRef(null);

  // Guards the duplication against a double click: its flight ends on another page, not here.
  const isDuplicatingRef = useRef(false);

  const [externalOrders, setExternalOrders] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasAccessibleOrders, setHasAccessibleOrders] = useState(true);

  const [selectedExternalOrders, setSelectedExternalOrders] = useState([]);

  /**
   * What narrows the list is held by the module context so opening an order — or creating one — and
   * coming back does not drop it. `appliedFilters` are the conditions sent in the list request body,
   * keyed by the API filter names.
   */
  const {
    search,
    setSearch,
    filters,
    setFilters,
    advancedFilters,
    setAdvancedFilters,
    appliedFilters,
    setAppliedFilters,
  } = useExternalOrdersContext();

  const { selectedStores, setSelectedStores } = usePersistedSelectedStores({ stores });

  // Trigger of the filters modal: what it applies is persisted, the "apply now" itself is not.
  const [applyFilters, setApplyFilters] = useState(false);

  const tabs = useMemo(() => getExternalOrderTabs(), []);

  const columns = useMemo(
    () => getExternalOrderColumns({ userLanguageCode, storeName, hasMultipleChannels }),
    [userLanguageCode, storeName, hasMultipleChannels],
  );

  const sortableKeys = useMemo(() => columns.map(({ propertyKey }) => propertyKey), [columns]);

  const {
    tab: selectedTabKey,
    currentPage,
    maxPerPage,
    orderBy,
    orderType,
    selectTab,
    setCurrentPage,
    setMaxPerPage,
    setOrderBy,
    setOrderType,
    resetParamsOfAllTabs,
  } = useExternalOrdersListParams({ sortableKeys });

  const { refreshIndex, performDeletion, performRowUpdate } = useListRowActions({
    deleteResource: externalOrderService.deleteExternalOrder,
    deleteSuccessMessage: i18next.t('EXTERNAL_ORDERS.LIST.DELETE_SUCCESS'),
    deleteErrorMessage: i18next.t('EXTERNAL_ORDERS.LIST.DELETE_ERROR'),
    staleStatuses: STALE_ORDER_ERROR_STATUSES,
    displayedRowCount: externalOrders.length,
    currentPage,
    setCurrentPage,
    setIsLoading,
    setSelectedRows: setSelectedExternalOrders,
  });

  const selectedStoreIds = useMemo(() => selectedStores.map(({ id }) => id), [selectedStores]);

  const selectedStoreIdsKey = useMemo(() => JSON.stringify(selectedStoreIds), [selectedStoreIds]);

  /**
   * The options of the client filter are not part of the list payload: they are the clients of the
   * selected stores, so they follow the store selection rather than the current page.
   */
  const handleExternalClientsError = useCallback(() => {
    displayErrorMessage(i18next.t('EXTERNAL_ORDERS.LIST.FETCH_ERROR'));
  }, [displayErrorMessage]);

  const externalClients = useExternalClientsOfStores({
    storeIds: selectedStoreIds,
    onError: handleExternalClientsError,
  });

  const columnsFilterList = useMemo(
    () => getExternalOrderFilterList({ externalClients, channels, hasMultipleChannels }),
    [externalClients, channels, hasMultipleChannels],
  );

  const appliedFiltersKey = useMemo(() => JSON.stringify(appliedFilters), [appliedFilters]);

  /**
   * What the filters modal owns: its advanced conditions and its store dropdown. The button cannot
   * tell on its own — its indicator does not survive the remount the tabs trigger — so the page
   * states it.
   */
  const hasAppliedFilters = !isEmpty(appliedFilters) || selectedStoreIds.length !== stores.length;

  // The empty state stands for "no order in my perimeter", so it can only be trusted while nothing
  // narrows the list — a tab, a search or a filter returning nothing is a plain "no result".
  const hasActiveNarrowing =
    !!search ||
    !isEmpty(appliedFilters) ||
    selectedTabKey !== EXTERNAL_ORDER_TAB.ALL ||
    selectedStoreIds.length !== stores.length;

  /**
   * Narrow the status condition with the active tab. Both cumulate, so a tab restricted to some
   * statuses only keeps the ones the status filter also selected.
   */
  const getStatusCondition = () => {
    const { statuses: tabStatuses } = tabs.find(({ key }) => key === selectedTabKey);
    const selectedStatuses = appliedFilters.status ? appliedFilters.status.inArray : null;

    if (!tabStatuses) {
      return selectedStatuses ? { inArray: selectedStatuses } : null;
    }

    if (!selectedStatuses) {
      return { inArray: tabStatuses };
    }

    return { inArray: tabStatuses.filter((status) => selectedStatuses.includes(status)) };
  };

  const buildListRequestBody = useCallback(() => {
    const statusCondition = getStatusCondition();

    const requestBody = {
      ...appliedFilters,
      storeIds: selectedStoreIds,
    };

    if (statusCondition) {
      requestBody.status = statusCondition;
    }

    return requestBody;
  }, [appliedFiltersKey, selectedStoreIdsKey, selectedTabKey, tabs]);

  useEffect(() => {
    // The store perimeter lands asynchronously in the store: querying before it is resolved would
    // both waste a request and make the page reason on a perimeter it does not know yet.
    if (areStoresLoading) {
      return;
    }

    let isCurrentQuery = true;

    const fetchExternalOrders = async () => {
      // The rows are about to be replaced: ListView drops their selected flag on its own, so the
      // selection driving the export has to be dropped here as well to stay in sync.
      setSelectedExternalOrders([]);

      /**
       * `hasAccessibleOrders` is deliberately left alone: it answers "does this client hold any
       * order at all", which only the route can tell. An empty store selection is not an answer, so
       * the list keeps its own "no result" empty state rather than the perimeter one.
       */
      if (!selectedStoreIds.length) {
        setExternalOrders([]);
        setTotalCount(0);
        setIsLoading(false);

        return;
      }

      setIsLoading(true);

      const requestBody = buildListRequestBody();

      try {
        const { data, count } = await externalOrderService.getPaginatedExternalOrders(
          { page: currentPage, limit: maxPerPage, search, orderBy, orderType },
          requestBody,
        );

        if (!isCurrentQuery) {
          return;
        }

        const rows = data ?? [];

        /**
         * A page beyond the last one — restored from the URL or from another tab whose result set
         * has shrunk since — comes back empty, and with it no count at all: the route carries the
         * count as a `COUNT(*) OVER ()` window on the returned rows. There is nothing to clamp
         * against, so the list starts over on its first page, exactly like the ListView paginator
         * does with an out-of-range `defaultCurrentPage`.
         */
        if (!rows.length && currentPage > FIRST_PAGE) {
          setCurrentPage(FIRST_PAGE);

          return;
        }

        setExternalOrders(formatExternalOrders(rows));
        setTotalCount(count ?? 0);

        if (!hasActiveNarrowing) {
          setHasAccessibleOrders((count ?? 0) > 0);
        }
      } catch {
        if (!isCurrentQuery) {
          return;
        }

        setExternalOrders([]);
        setTotalCount(0);

        displayErrorMessage(i18next.t('EXTERNAL_ORDERS.LIST.FETCH_ERROR'));
      } finally {
        if (isCurrentQuery) {
          setIsLoading(false);
        }
      }
    };

    fetchExternalOrders();

    return () => {
      isCurrentQuery = false;
    };
  }, [
    areStoresLoading,
    selectedTabKey,
    search,
    orderBy,
    orderType,
    currentPage,
    maxPerPage,
    selectedStoreIdsKey,
    appliedFiltersKey,
    refreshIndex,
  ]);

  /**
   * Narrowing the list invalidates the pagination and the sort remembered for every tab: their pages
   * would point at results that moved or are gone. The ListView paginator holds its own page, hence
   * the reset on the ref as well.
   */
  const resetPagination = () => {
    if (listViewRef.current) {
      listViewRef.current.resetPagination();
    }

    resetParamsOfAllTabs();
  };

  /**
   * Turn the applied filters into the request body conditions. Each advanced filter carries the
   * operation builder of its type, which maps its selection to the drizzle descriptor the list
   * route expects (e.g. `{ inArray: [...] }`); the modal only keeps the displayed, valued filters.
   * The delivery time bounds are custom dropdowns of the modal and build their condition apart.
   */
  useEffect(() => {
    if (!applyFilters) {
      return;
    }

    setApplyFilters(false);
    resetPagination();

    const nextFilters = (advancedFilters || []).reduce(
      (result, { getQueryParamOperation, propertyKey, value }) => {
        if (!getQueryParamOperation) {
          return result;
        }

        const condition = getQueryParamOperation(propertyKey, value);

        if (hasMissingOperand(condition)) {
          return result;
        }

        result[propertyKey] = condition;

        return result;
      },
      {},
    );

    setAppliedFilters(nextFilters);
  }, [advancedFilters, applyFilters]);

  const handleSelectTab = (tabKey) => {
    if (tabKey === selectedTabKey) {
      return;
    }

    // The fetch effect only flips the loading flag on the next cycle: raising it now prevents the
    // perimeter empty state from flashing between the tab click and the fetch start.
    setIsLoading(true);
    selectTab(tabKey);
  };

  const handleSearchInputChange = (value) => {
    setSearch(value);
    resetPagination();
  };

  const handleStoreFilterChange = (items) => {
    setSelectedStores(items);
    resetPagination();
  };

  /**
   * Duplicate an order (COR-1691): nothing is created here — the route answers a prefilled order
   * payload, and the creation form opens on it. The order only exists once saved or validated from
   * there, hence no success toaster on this side.
   */
  const handleDuplicate = async (externalOrder) => {
    if (isDuplicatingRef.current) {
      return;
    }

    isDuplicatingRef.current = true;
    pageLoading();

    try {
      const duplicatedExternalOrder = await externalOrderService.getDuplicatedExternalOrderPayload(
        externalOrder.id,
      );

      history.push(EXTERNAL_FEATURE_PATHS.EXTERNAL_ORDERS_CREATION, {
        ...buildOrdersListReturnState(),
        [DUPLICATED_ORDER_STATE_KEY]: duplicatedExternalOrder,
      });
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_ORDERS.LIST.DUPLICATE_ERROR'));
    } finally {
      isDuplicatingRef.current = false;
      pageLoaded();
    }
  };

  const handleDelete = useCallback(
    (externalOrder) => {
      openModal(
        getDeleteOrderModal({
          onConfirm: () => {
            performDeletion(externalOrder);
          },
        }),
      );
    },
    [performDeletion],
  );

  /**
   * Play a status transition once confirmed (PATCH, COR-1686). The stale statuses resynchronize the
   * list exactly as a deletion does: a 409 means the order already left the status the action
   * started from, a 404 that it is gone or unreachable.
   */
  const handleStatusTransition = ({
    externalOrder,
    status,
    getModal,
    successMessage,
    errorMessage,
  }) => {
    openModal(
      getModal({
        onConfirm: () => {
          performRowUpdate({
            request: () => externalOrderService.updateExternalOrderStatus(externalOrder.id, status),
            successMessage,
            errorMessage,
          });
        },
      }),
    );
  };

  const handleRevertToDraft = (externalOrder) => {
    handleStatusTransition({
      externalOrder,
      status: EXTERNAL_ORDER_STATUS.DRAFT,
      getModal: getRevertToDraftModal,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_DRAFT_ERROR'),
    });
  };

  const handleRevertToToCome = (externalOrder) => {
    handleStatusTransition({
      externalOrder,
      status: EXTERNAL_ORDER_STATUS.TO_COME,
      getModal: getRevertToToComeModal,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_TO_COME_ERROR'),
    });
  };

  const handleRevertToInPreparation = (externalOrder) => {
    handleStatusTransition({
      externalOrder,
      status: EXTERNAL_ORDER_STATUS.IN_PREPARATION,
      getModal: getRevertToInPreparationModal,
      successMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_SUCCESS'),
      errorMessage: i18next.t('EXTERNAL_ORDERS.LIST.REVERT_TO_IN_PREPARATION_ERROR'),
    });
  };

  /**
   * The URL of the list travels with the form so leaving it comes back here, on the very tab, page
   * and sort the user opened it from rather than on the bare list.
   */
  const goToCreation = () => {
    history.push(EXTERNAL_FEATURE_PATHS.EXTERNAL_ORDERS_CREATION, buildOrdersListReturnState());
  };

  const goToOrderDetails = ({ id }) => {
    history.push(
      EXTERNAL_FEATURE_PATHS.EXTERNAL_ORDERS_DETAILS.replace(':id', id),
      buildOrdersListReturnState(),
    );
  };

  const handleExportList = useCallback(async () => {
    const exportParams = { userLanguageCode, storeName, hasMultipleChannels, currency };

    if (selectedExternalOrders.length) {
      exportExternalOrders(selectedExternalOrders, exportParams);

      return;
    }

    if (!totalCount) {
      return;
    }

    pageLoading();

    try {
      const { data } = await externalOrderService.getPaginatedExternalOrders(
        { page: FIRST_PAGE, limit: totalCount, search, orderBy, orderType },
        buildListRequestBody(),
      );

      exportExternalOrders(formatExternalOrders(data ?? []), exportParams);
    } catch {
      displayErrorMessage(i18next.t('EXTERNAL_ORDERS.LIST.EXPORT_ERROR'));
    } finally {
      pageLoaded();
    }
  }, [
    selectedExternalOrders,
    totalCount,
    search,
    orderBy,
    orderType,
    buildListRequestBody,
    userLanguageCode,
    storeName,
    hasMultipleChannels,
    currency,
  ]);

  const actions = useMemo(
    () =>
      getActions({
        canCreateDraft,
        onCreate: goToCreation,
        onExport: handleExportList,
      }),
    [canCreateDraft, handleExportList],
  );

  const rowActions = useMemo(
    () =>
      getRowActions({
        canCreateDraft,
        canDeleteDraft,
        canRevertToDraft,
        canEditToComeAndInPreparation,
        canRevertToInPreparation,
        onDuplicate: handleDuplicate,
        onDelete: handleDelete,
        onRevertToDraft: handleRevertToDraft,
        onRevertToToCome: handleRevertToToCome,
        onRevertToInPreparation: handleRevertToInPreparation,
      }),
    [
      canCreateDraft,
      canDeleteDraft,
      canRevertToDraft,
      canEditToComeAndInPreparation,
      canRevertToInPreparation,
      handleDelete,
    ],
  );

  const renderEmptyPerimeterState = () => (
    <GeneralEmptyStateListView
      icon={'/images/inpulse/first-order.svg'}
      renderAction={
        canCreateDraft
          ? () => (
              <Button
                color={'inpulse-default'}
                handleClick={goToCreation}
                icon={'/images/inpulse/add-white-small.svg'}
                label={i18next.t('GENERAL.CREATE')}
              />
            )
          : null
      }
      subtitle={i18next.t(
        canCreateDraft
          ? 'EXTERNAL_ORDERS.LIST.EMPTY_STATE_SUBTITLE_WITH_CREATION'
          : 'EXTERNAL_ORDERS.LIST.EMPTY_STATE_SUBTITLE',
        { storeName: getClientStoreNameTranslation(storeName, true) },
      )}
      title={i18next.t('EXTERNAL_ORDERS.LIST.EMPTY_STATE_TITLE')}
    />
  );

  const renderList = () => (
    <ListView
      actionOnClick={goToOrderDetails}
      actions={actions}
      columns={columns}
      countElements={totalCount}
      data={externalOrders}
      defaultCurrentPage={currentPage}
      defaultMaxPerPage={maxPerPage}
      defaultOrderBy={orderBy}
      defaultOrderType={orderType}
      defaultSearchInput={search}
      handleCurrentPageChange={setCurrentPage}
      handleMaxPerPageChange={setMaxPerPage}
      handleOrderByChange={setOrderBy}
      handleOrderTypeChange={setOrderType}
      handleSearchInputChange={handleSearchInputChange}
      isLoading={isLoading}
      // ListView seeds its own page from `defaultCurrentPage` and only resyncs it when its result
      // count changes: remounting on tab change is what makes the restored page and sort land.
      key={selectedTabKey}
      languageCode={userLanguageCode}
      padding={'24px 24px 0px 24px'}
      placeholderShape={i18next.t('GENERAL.SEARCH')}
      ref={listViewRef}
      renderEmptyState={() => <EmptyState label={i18next.t('EXTERNAL_ORDERS.LIST.NO_RESULT')} />}
      renderFilterButton={() => (
        <DeepsightFiltersButton
          advancedFilters={advancedFilters}
          applyFilters={applyFilters}
          columnsFilterList={columnsFilterList}
          filters={filters}
          hasAppliedFilters={hasAppliedFilters}
          isLoading={isLoading}
          readOnly={isLoading}
          selectedStores={selectedStores}
          setAdvancedFilters={setAdvancedFilters}
          setApplyFilters={setApplyFilters}
          setFilters={setFilters}
          setSelectedStores={handleStoreFilterChange}
          stores={stores}
          textFilterButton={i18next.t('GENERAL.LIST_VIEW_FILTER_BUTTON')}
        />
      )}
      rowActions={rowActions}
      setSelectedItems={setSelectedExternalOrders}
      onQueryParamsChange={enableServerSideQuerying}
    />
  );

  return (
    <PageContainer>
      <NavigationBreadCrumb featurePath={path} />
      <StatusTabs selectedTabKey={selectedTabKey} tabs={tabs} onSelectTab={handleSelectTab} />
      <Content>
        {!isLoading && !hasAccessibleOrders ? renderEmptyPerimeterState() : renderList()}
      </Content>
    </PageContainer>
  );
};

const mapStateToProps = (state) => {
  const authorizedActions = getAuthorizedActions(
    state.baseReducer.userRights,
    EXTERNAL_ORDERS_PATH,
  );

  const { storeName, clientChannels, hasMultipleChannels } = getClientInfo(state.baseReducer.user);

  const isDeepsight = state.baseReducer.user.isDeepsight;

  return {
    stores: state.baseReducer.activeStores,
    // Derived by the loading reducer from the STORES_REQUEST / STORES_SUCCESS actions.
    areStoresLoading: !!state.loadingReducer.STORES,
    storeName,
    channels: clientChannels,
    hasMultipleChannels,
    currency: state.baseReducer.currency,
    userLanguageCode: state.baseReducer.user?.lnkLanguageAccountrel?.code,
    canCreateDraft: isDeepsight || canCreateAndEditDraftExternalOrder(authorizedActions),
    canDeleteDraft: isDeepsight || canDeleteDraftExternalOrder(authorizedActions),
    canRevertToDraft: isDeepsight || canRevertExternalOrderToDraft(authorizedActions),
    canEditToComeAndInPreparation:
      isDeepsight || canEditToComeAndInPreparationExternalOrder(authorizedActions),
    canRevertToInPreparation:
      isDeepsight || canRevertExternalOrderToInPreparation(authorizedActions),
  };
};

const mapDispatchToProps = (dispatch) => ({
  openModal: (params) => {
    dispatch(openGenericModal(params));
  },
  displayErrorMessage: (message) => {
    dispatch(showErrorMessage(message));
  },
  pageLoading: () => {
    dispatch(loading());
  },
  pageLoaded: () => {
    dispatch(loadingSuccess());
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(ExternalOrders);
